import { motion } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import StillButton from './ui/StillButton';
import { useVideoTransition } from '../hooks/useVideoTransition';
import { useNavigate } from 'react-router-dom';
import { useVideoPreloader } from '../hooks/useVideoPreloader';
import { videoConfigs } from '../config/videos';
import { useOverlay, MasterOverlayContext } from '../App';
import { useContext } from 'react';

// Master overlay intro video
const OVERLAY_CONFIG = {
    TIMESTAMP: 15, // Show overlay after 15 seconds of intro video
    ENABLED: true
};

const LandingScreen = ({ audioMuted, toggleAudio, showSoundPrompt, setShowSoundPrompt }) => {
    const { handleNavigate, setIsPlaying, videoState, isPlaying } = useVideoTransition();
    const { preloadVideo } = useVideoPreloader();
    const navigate = useNavigate();
    const { toggleOverlay } = useOverlay();
    const [contentVisible, setContentVisible] = useState(true);
    const [hasInteracted, setHasInteracted] = useState(false);
    const fadeTimeoutRef = useRef(null);
    const videoRef = useRef(null);
    const overlayTimerRef = useRef(null);
    const timestampListenerAddedRef = useRef(false);
    const overlayVisibleRef = useRef(false);
    const { overlayVisible: masterOverlayVisible } = useContext(MasterOverlayContext);
    
    // Function to force the overlay to be visible
    const forceShowOverlay = () => {
        // Set our local ref first
        overlayVisibleRef.current = true;
        
        // Set the flag in sessionStorage first
        sessionStorage.setItem('overlayVisible', 'true');
        
        // Then update the global state - force it to true
        toggleOverlay(true);
        //console.log('Force showing overlay - setting global state to visible');
        
        // Force animation to start after overlay has faded in
        setTimeout(() => {
            //console.log('LandingScreen: Forcing animation to start after overlay fade-in');
            
            // Dispatch a custom event to trigger animation
            const event = new CustomEvent('startOverlayAnimation');
            window.dispatchEvent(event);
        }, 1500); // 1.5 second delay to ensure overlay has faded in
    };
    
    // Function to hide the overlay
    const hideOverlay = () => {
        // Only hide if we're not in the middle of showing it via timestamp
        if (!timestampListenerAddedRef.current || videoRef.current?.currentTime < OVERLAY_CONFIG.TIMESTAMP) {
            overlayVisibleRef.current = false;
            toggleOverlay(false);
            
            // Clear the interval if it exists
            if (overlayTimerRef.current) {
                clearInterval(overlayTimerRef.current);
                overlayTimerRef.current = null;
            }
        }
    };
    
    // Hide the header when this component mounts
    useEffect(() => {
        // Get the refresh flag from our other useEffect
        const isLikelyRefresh = sessionStorage.getItem('isRefresh') === 'true';
        
        // Check if the overlay should be visible based on sessionStorage
        const shouldBeVisible = sessionStorage.getItem('overlayVisible') === 'true';
        
        if (shouldBeVisible && !isLikelyRefresh) {
            // If the overlay should be visible and this is not a refresh, force it to be visible
            //console.log('LandingScreen: Overlay should be visible based on sessionStorage, forcing visibility');
            forceShowOverlay();
        } else {
            // Otherwise, initially hide the overlay
            hideOverlay();
            //console.log('LandingScreen: Initially hiding overlay');
        }
        
        // Clean up when unmounting
        return () => {
            // Don't hide the overlay on unmount, as we might be transitioning to another screen
            // and want to keep the overlay visible
            //console.log('LandingScreen: Unmounting, keeping overlay state');
            
            // Just clear any intervals
            if (overlayTimerRef.current) {
                clearInterval(overlayTimerRef.current);
                overlayTimerRef.current = null;
            }
        };
    }, []);
    
    // Set a flag to detect page refreshes
    useEffect(() => {
        // Set a timestamp in sessionStorage when the component mounts
        const now = Date.now();
        const lastMountTime = parseInt(sessionStorage.getItem('landingScreenMountTime'), 10) || 0;
        const timeSinceLastMount = now - lastMountTime;
        
        // If the component was mounted less than 3 seconds ago, it's likely a refresh
        const isLikelyRefresh = timeSinceLastMount < 3000;
        
        // Store the refresh state in sessionStorage for other useEffects to access
        sessionStorage.setItem('isRefresh', isLikelyRefresh.toString());
        
        if (isLikelyRefresh) {
            //console.log('LandingScreen: Likely page refresh detected, clearing overlay visibility flag');
            sessionStorage.removeItem('overlayVisible');
            hideOverlay();
        }
        
        // Update the mount time
        sessionStorage.setItem('landingScreenMountTime', now.toString());
        
        // Also set a flag to indicate this is a fresh page load
        if (!sessionStorage.getItem('pageLoaded')) {
            //console.log('LandingScreen: First page load detected');
            sessionStorage.setItem('pageLoaded', 'true');
            sessionStorage.removeItem('overlayVisible');
            hideOverlay();
        }
        
        // Clear the refresh flag when the component unmounts
        return () => {
            sessionStorage.removeItem('isRefresh');
        };
    }, []);
    
    // Set up video timeupdate listener to check for timestamp marker
    useEffect(() => {
        // Only proceed if timestamp triggering is enabled
        if (!OVERLAY_CONFIG.ENABLED) return;
        
        // Function to find and set up the main video element
        const setupVideoTimeTracking = () => {
            // If we've already set up the listener, don't do it again
            if (timestampListenerAddedRef.current) return;
            
            // Find the main video element
            const videos = document.querySelectorAll('video');
            if (videos.length === 0) {
                // Incase no video is found
                setTimeout(setupVideoTimeTracking, 100);
                return;
            }
            
            // Get the main video
            const mainVideo = videos[0];
            videoRef.current = mainVideo;
            
            // Set up the timeupdate listener
            const handleTimeUpdate = () => {
                const video = videoRef.current;
                if (!video) return;
                
                // Check time
                if (video.currentTime >= OVERLAY_CONFIG.TIMESTAMP) {
                    // Log for debugging
                    //console.log('Timestamp reached:', video.currentTime, 'showing overlay');
                    
                    // Set the flag in sessionStorage first
                    sessionStorage.setItem('overlayVisible', 'true');
                    
                    // Show the overlay
                    forceShowOverlay();
                    
                    // Remove the listener after triggering once
                    video.removeEventListener('timeupdate', handleTimeUpdate);
                    
                    // Log for debugging
                    //console.log('Overlay shown at timestamp:', video.currentTime);
                }
            };
            
            // Add the listener
            mainVideo.addEventListener('timeupdate', handleTimeUpdate);
            timestampListenerAddedRef.current = true;
        };
        
        // Only set up on the landing page
        if (window.location.pathname === '/') {
            // Initial setup
            setupVideoTimeTracking();
            
            // Also set up when the video source might change
            window.addEventListener('videoSourceChanged', setupVideoTimeTracking);
        }
        
        // Clean up
        return () => {
            if (videoRef.current) {
                videoRef.current.removeEventListener('timeupdate', () => {});
            }
            window.removeEventListener('videoSourceChanged', setupVideoTimeTracking);
            timestampListenerAddedRef.current = false;
        };
    }, []);
    
    // Preload the passive screen video immediately
    useEffect(() => {
        const passiveScreenVideo = videoConfigs.find(video => video.route === '/space');
        if (passiveScreenVideo?.src) {
            //console.log('Preloading passive screen video:', passiveScreenVideo.src);
            preloadVideo(passiveScreenVideo.src);
        }
    }, [preloadVideo]);
    
    // Set up a timer to fade out content after 30 seconds
    useEffect(() => {
        fadeTimeoutRef.current = setTimeout(() => {
            setContentVisible(false);
        }, 14000); // 14 seconds
        
        return () => {
            if (fadeTimeoutRef.current) {
                clearTimeout(fadeTimeoutRef.current);
            }
        };
    }, []);
    
    // Video ended event handler
    useEffect(() => {
        const handleIntroVideoEnded = () => {
            //console.log('Received introVideoEnded event');
            
            // The video source is already being updated in VideoBackground.jsx
            // We just need to navigate to the new route
            //console.log('Navigating to passive screen after intro video ended');
            
            // Use the navigate function directly instead of handleNavigate
            // since VideoBackground is already handling the video transition
            navigate('/space');
        };
        
        window.addEventListener('introVideoEnded', handleIntroVideoEnded);
        
        return () => {
            window.removeEventListener('introVideoEnded', handleIntroVideoEnded);
        };
    }, [navigate]);
    
    // Disable looping for intro video
    useEffect(() => {
        // Find all video elements
        const videos = document.querySelectorAll('video');
        videos.forEach(video => {
            if (window.location.pathname === '/') {
                // Only log in development
                if (process.env.NODE_ENV === 'development') {
                    //console.log('Disabling loop for intro video');
                }
                video.loop = false;
                
                // If we're returning to the landing screen, ensure the video plays from the beginning
                if (video.currentTime > 0) {
                    // Only log in development
                    if (process.env.NODE_ENV === 'development') {
                        //console.log('Resetting intro video to beginning');
                    }
                    video.currentTime = 0;
                    video.play().catch(err => {
                        // Only log in development
                        if (process.env.NODE_ENV === 'development') {
                            //console.log('Could not autoplay intro video:', err);
                        }
                    });
                }
            }
        });
    }, []);
    
    // Set up intro video when component mounts
    useEffect(() => {
        // Only log in development
        if (process.env.NODE_ENV === 'development') {
            //console.log('LandingScreen mounted, setting up intro video');
        }
        
        // Find the intro video config
        const introVideo = videoConfigs.find(v => v.route === '/');
        if (!introVideo) return;
        
        // Find all video elements
        const videos = document.querySelectorAll('video');
        if (videos.length === 0) return;
        
        const video = videos[0];
        
        // Check if we need to update the video source
        if (!video.src.includes(introVideo.src.split('/').pop())) {
            // Only log in development
            if (process.env.NODE_ENV === 'development') {
                //console.log('Setting intro video source:', introVideo.src);
            }
            
            // Update the video source
            video.src = introVideo.src;
            video.load();
        }
        
        // Set video attributes
        video.loop = false;
        video.autoplay = true;
        video.playsInline = true;
        
        // Reset to beginning if needed
        if (video.currentTime > 0) {
            video.currentTime = 0;
        }
        
        // Try to play the video with a slight delay to ensure it's loaded
        setTimeout(() => {
            // Only log in development
            if (process.env.NODE_ENV === 'development') {
                //console.log('Attempting to play intro video');
            }
            
            const playPromise = video.play();
            if (playPromise !== undefined) {
                playPromise.catch(err => {
                    // Only log in development
                    if (process.env.NODE_ENV === 'development') {
                        //console.log('Could not autoplay intro video:', err);
                    }
                    
                    // If autoplay fails, set up a user interaction handler
                    const userInteractionHandler = () => {
                        video.play().catch(e => {
                            // Only log in development
                            if (process.env.NODE_ENV === 'development') {
                                //console.log('Still could not play video after interaction:', e);
                            }
                        });
                        
                        // Remove the event listeners after first interaction
                        ['click', 'touchstart', 'keydown'].forEach(event => {
                            document.removeEventListener(event, userInteractionHandler);
                        });
                    };
                    
                    // Add event listeners for common user interactions
                    ['click', 'touchstart', 'keydown'].forEach(event => {
                        document.addEventListener(event, userInteractionHandler);
                    });
                });
            }
        }, 100);
    }, []);
    
    // Check if we're returning from landing screen via logo button
    useEffect(() => {
        // If we're on the landing page, reset the animation state
        if (window.location.pathname === '/') {
            sessionStorage.removeItem('animationCompleted');
            sessionStorage.removeItem('lastDisplayedCount');
            
            // But don't remove the overlayVisible flag - we want to keep that
            //console.log('Landing page - keeping overlayVisible flag:', sessionStorage.getItem('overlayVisible'));
        }
    }, [window.location.pathname]);
    
    // Clear overlay visibility flag on page refresh
    useEffect(() => {
        // Function to handle page refresh
        const handleBeforeUnload = () => {
            // Only clear the flag if we're on the landing page
            if (window.location.pathname === '/') {
                //console.log('LandingScreen: Page refresh/unload detected, clearing overlay visibility flag');
                sessionStorage.removeItem('overlayVisible');
            }
        };
        
        // Add event listener for beforeunload
        window.addEventListener('beforeunload', handleBeforeUnload);
        
        // Clean up
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);
    
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 3,
                staggerChildren: 1,
                ease: "easeInOut"
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 5,
                staggerChildren: 3,
                ease: "easeInOut"
            },
        },
    };

    const handleContainerClick = () => {
        setShowSoundPrompt(false);
        setIsPlaying(true);
        setHasInteracted(true);
        
        if (typeof window !== 'undefined') {
            window.STILL_LOVED_USER_INTERACTED = true;
        }
        
        // Do not toggle audio here, let the specific buttons handle it
    };

    const handleCloseClick = (withSound) => {
        if (fadeTimeoutRef.current) {
            clearTimeout(fadeTimeoutRef.current);
        }
        
        setContentVisible(false);
        setIsPlaying(true);
        setHasInteracted(true); // Ensure interaction is registered

        if (typeof window !== 'undefined') {
            window.STILL_LOVED_USER_INTERACTED = true;
        }
        
        if (withSound) {
            if (audioMuted) {
                toggleAudio(); // Turn sound on if it was off
            }
        } else {
            // Begin without sound
            if (!audioMuted) {
                toggleAudio(); // Turn sound off if it was on
            }
        }
    };

    return (
        <motion.div 
            className="relative flex items-center justify-center min-h-full"
            variants={containerVariants}
            initial="hidden"
            animate={contentVisible ? "visible" : "hidden"}
            exit="hidden"
            onClick={handleContainerClick}
        >
            <motion.div 
                className="relative z-10 flex flex-col items-center justify-center text-center text-still-pink p-6 py-12 sm:py-20"
                variants={containerVariants}
                animate={contentVisible ? "visible" : "hidden"}
            >
                <motion.h1 
                    className="text-4xl sm:text-5xl md:text-5xl lg:text-5xl font-bold mb-6 mt-[50px] opacity-80 mix-blend-screen" 
                    variants={itemVariants}
                >
                    STILL REMEMBERED.<br/>STILL LOVED.<br/>
                    <span>STILL </span>
                    <span className="text-white text-glow-still animate-fade-in">SHINING.</span>
                </motion.h1>
                <motion.h2 
                    className="text-still-white text-base sm:text-base md:text-base mb-6 opacity-8 whitespace-pre-line max-w-[390px] sm:max-w-[500px] md:max-w-[500px] lg:max-w-[800px]" 
                    variants={itemVariants}
                >
                    Welcome to Still Loved - a shining tribute to every baby lost in early pregnancy.
                    
                    Here, families can honour their precious babies by adding a star in their memory, 
                    turning a life measured in weeks, into an enduring love measured in weeks.
                </motion.h2>
                <motion.h3 
                    className="text-still-white text-base sm:text-base md:text-base mb-12 opacity-80" 
                    variants={itemVariants}
                >
                    Travel through the nebula and take a journey of love and remembrance.
                </motion.h3>
                <motion.div variants={itemVariants} className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                    <StillButton
                        onClick={() => handleCloseClick(true)}
                        aria-label="Begin with sound"
                    >
                        BEGIN WITH SOUND
                    </StillButton>
                    <StillButton
                        onClick={() => handleCloseClick(false)}
                        aria-label="Begin without sound"
                    >
                        BEGIN WITHOUT SOUND
                    </StillButton>
                </motion.div>
            </motion.div>
        </motion.div>
    );
};

export default LandingScreen; 